import { Component, inject, Input, OnChanges, OnInit, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { ButtonComponent } from '@ppl/ui';
import {  ListTableComponent  } from '@ppl/ui';
import { DialogConfig, DialogService } from '../../../../shared/services/dialog.service';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgZorroModule } from "../../../../shared/modules/ng-zorro/ng-zorro.module";
import { NzFormDirective } from 'ng-zorro-antd/form';
import { CommonModule, TitleCasePipe } from '@angular/common';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { ProviderService } from '../../../../services/provider.service';
import { UserService } from '../../../../services/user.service';
import { DateTimeFormatterService } from '../../../../shared/utils/date-time-formatter.service';
import { AuthorizationsDetailsService } from '../../../../services/authorizations-service.service';
import { ListService } from '../../../../shared/services/list.service';
import { TableRowActions } from '../../../../shared/interfaces/actions';
import { TableRowActionsModel } from '../../../../shared/interfaces/list.interface';
import { toServiceDetailRow } from '../../../../shared/utils/auth-service-detail.util';
import { AuthServiceDetailFormComponent, AuthServiceDetailPayload } from '../auth-service-detail-form/auth-service-detail-form.component';

@Component({
  selector: 'ppl-auth-allocation-list',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonComponent,
    ListTableComponent,
    NgZorroModule,
    FormsModule,
    NzFormDirective,
    NzDatePickerModule,
    AuthServiceDetailFormComponent,
  ],
  templateUrl: './auth-allocation-list.component.html',
  styleUrl: './auth-allocation-list.component.css'
})
export class AuthAllocationListComponent implements OnInit, OnChanges {
  @Input() authData: any;
  @Input() authId: any;
  @Input() consumer: any;
  @Input() mode: any;
  @ViewChild('authAllocationAddDialog') authAllocationAddDialog!: TemplateRef<any>;
  @ViewChild('confirmationDialog') confirmationDialog!: TemplateRef<any>;
  @ViewChild(ListTableComponent) allocationTable?: ListTableComponent;
  @ViewChild('serviceDetailDialog') serviceDetailDialog!: TemplateRef<any>;
  @ViewChild('serviceDetailConfirmationDialog') serviceDetailConfirmationDialog!: TemplateRef<any>;

  private dialogSvc = inject(DialogService);

  // Row actions come from the projected #rowActions template so Add Service Detail
  // can sit after Edit and Deactivate.
  actionButtons: TableRowActionsModel = new TableRowActions({});
  allocationCols: any[] = [
    {
      field: 'pplProviderId',
      header: 'Provider PPL ID',
      jsonpath: 'pplProviderId',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'providerName',
      header: 'Provider Name',
      jsonpath: 'providerName',
      isDisplayedAtHeader: 'n',
      isSearchable: 'n',
    },
    {
      field: 'startDate',
      header: 'Start Date',
      jsonpath: 'startDate',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'endDate',
      header: 'End Date',
      jsonpath: 'endDate',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'amount',
      header: 'Allocated Dollars',
      jsonpath: 'amount',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'usedAmount',
      header: 'Used Dollars',
      jsonpath: 'usedAmount',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
    {
      field: 'status',
      header: 'Status',
      jsonpath: 'status',
      isDisplayedAtHeader: 'y',
      isSearchable: 'n',
    },
  ];

  // Expanded (per-allocation) service detail columns. The list-table's own line item
  // action is a hardcoded View button, so Edit is rendered as a clickable cell and
  // handled through rowCellClicked.
  serviceDetailCols: any[] = [
    {
      field: 'serviceDetail',
      header: 'Service Detail',
      jsonpath: 'serviceDetail',
    },
    {
      field: 'frequencyLabel',
      header: 'Frequency',
      jsonpath: 'frequencyLabel',
    },
    {
      field: 'units',
      header: 'Unit',
      jsonpath: 'units',
    },
    {
      field: 'priceDisplay',
      header: 'Price per Unit',
      jsonpath: 'priceDisplay',
    },
    {
      field: 'editAction',
      header: 'Actions',
      jsonpath: 'editAction',
      isClickable: true,
    },
  ];

  addAuthAllocationDialog: any;
  confirmDialogId: any;
  addAuthAllocationForm!: FormGroup;
  authAllocationData: any;
  currentAuthorizationProviderId = null;
  paList: any = [];
  programName!: string;
  providers: any[] = [
    { providerId: 0, providerPplId: 'TBD' },
  ];

  addServiceDetailDialog: any;
  confirmServiceDetailDialogId: any;
  /** Allocation row the service detail dialog is currently working against. */
  currentServiceAllocation: any = null;
  /** Null while adding, populated while editing an existing service detail. */
  currentServiceDetail: any = null;

  /** Inactive allocations stay read-only — no Edit, Deactivate or Add Service Detail. */
  actionsDisabler = (row: any) => !row?.active;

  constructor(
    private authorizationService: AuthorizationsDetailsService,
    private dateTimeFormatter: DateTimeFormatterService,
    private fb: FormBuilder,
    private listService: ListService,
    private providerService: ProviderService,
    private userService: UserService,
  ) { }

  ngOnInit(): void {
    const program = this.userService.getSelectedProgram();
    this.programName = program?.program_code ?? '';

    if (this.consumer?.participantId || this.authData?.participantId) {
      this.providerService
        .getProviderList(this.programName, this.consumer?.participantId ?? this.authData?.participantId)
        .subscribe((data: any) => {
          if (data?.responsedata) {
            this.providers = [...this.providers, ...data.responsedata];
          }
          else{
            this.providers = [...this.providers];
          }
        });
    }

    this.addAuthAllocationForm = this.fb.group({
      pa: [null, Validators.required],
      // The pickers need a real Date. Handing them the API's `yyyy-MM-dd` string makes
      // ng-zorro parse it as UTC midnight, which renders as the previous day locally.
      authStartDate: [this.toDateOnly(this.authData.startDate), Validators.required],
      authEndDate: [this.toDateOnly(this.authData.endDate), Validators.required],
      units: [null],
      otherRate: [false],
    });

    this.fetchAuthAllocation();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Recalculate remaining allocation whenever authData changes
    if (changes['authData'] && this.addAuthAllocationForm) {
      this.calculateTotalAllocatedDollars();
    }

    // Editing the authorization flips `mode`, which decides whether a row offers Edit and
    // Deactivate. The rows were built once from the list response, so they have to be rebuilt
    // here or they keep the actions that were right when they were fetched.
    if (changes['mode']) {
      this.refreshRowActions();
    }
  }

  /**
   * Row actions for one allocation. `mode` is the parent's disable flag rather than a mode
   * string — truthy means the authorization is read-only, so the row offers nothing. An
   * inactive allocation is never editable regardless.
   */
  private buildRowActions(row: any): any {
    const actions: any = {};

    if (row?.active && !this.mode) {
      actions.deactivate = true;
      actions.edit = true;
    }

    return actions;
  }

  /** Re-evaluate every loaded row against the current `mode`. */
  private refreshRowActions(): void {
    if (!Array.isArray(this.authAllocationData)) {
      return;
    }

    // A new array reference: ppl-list-table is OnPush and ignores in-place mutation.
    this.authAllocationData = this.authAllocationData.map((row: any) => ({
      ...row,
      actions: this.buildRowActions(row),
    }));
  }

  buildRequestPayload(pageName: string, orderBy: string, searchParams: any, startingPageNumber = 1) {
    const payload: any = {
      pageName: pageName,
      programCode: this.programName,
      condition: 'and',
      recordsPerPage: 20,
      startingPageNumber: startingPageNumber,
      searchParam: searchParams,
      orderByColumn: orderBy ? [orderBy] : [],
      orderBy: ['desc'],
    };
    return payload;
  }

  fetchAuthAllocation(startingPageNumber = 1, currentPage = 0) {
    if (startingPageNumber == 1) {
      this.authAllocationData = [];
    }

    const processedPayload = this.buildRequestPayload(
      'consumer-auth-prvdr-rate',
      'modifiedDate',
      [{ jsonPath: "authorizationId", value: this.authId ?? this.authData?.authorizationId, operator: "Equal" }],
      startingPageNumber
    );

    this.listService
      .getListData(processedPayload, '')
      .subscribe((data: any) => {
        const records = data?.responsedata?.map((data: any) => {
          if (data.providerId === '0') {
            data.pplProviderId = 'TBD';
          }
          data.status = data.active ? 'Active' : 'Inactive';
          data.actions = this.buildRowActions(data);

          return data;
        });

        if (startingPageNumber > 1) {
          this.authAllocationData = [...this.authAllocationData, ...records];
        } else {
          this.authAllocationData = records;
        }

        this.calculateTotalAllocatedDollars();
      });
  }

  addAllocation(action: any) {
    const dialogConfig: DialogConfig = {
      title: `${action} Provider Auth Allocation`,
      size: 'medium',
      position: 'top',
      okText: `${action}`,
      showClose: true,
      showButtons: false,
    };

    this.addAuthAllocationDialog = this.dialogSvc.openDialog(
      this.authAllocationAddDialog,
      dialogConfig,
      () => this.handleCancel(),
      () => this.handleCancel()
    );
  }

  calculateTotalAllocatedDollars() {
    if (this.authAllocationData && this.authAllocationData.length > 0) {
      const existingTotalAllocated = this.authAllocationData.filter((e: any) => e.active).reduce(
        (sum: number, alloc: any) => sum + (alloc.amount || 0),
        0
      );
      const remainingAllocation = (Number(this.authData.units) || 0) - existingTotalAllocated;
      this.addAuthAllocationForm.get('units')?.setValue(remainingAllocation > 0 ? remainingAllocation : null);
    } else if (this.authData?.units) {
      this.addAuthAllocationForm.get('units')?.setValue(this.authData?.units);
    }
  }

  onAuthAllocationActionPerformed(action: any) {
    if (this.addAuthAllocationForm.invalid) {
      this.addAuthAllocationForm.markAllAsTouched();
      return;
    }

    const validationResult = this.validateAllocationForm();
    if (validationResult.error) {
      this.addAuthAllocationForm.get(validationResult.control)?.setErrors({
        customError: validationResult.message
      });
      return;
    }

    // toDateOnly rather than new Date(): formatToString reads local date components, so a
    // date-only string parsed as UTC midnight came back out a day early.
    const authStartDate = this.toDateOnly(this.addAuthAllocationForm.value.authStartDate);
    const authEndDate = this.toDateOnly(this.addAuthAllocationForm.value.authEndDate);
    const payload = {
      authorizationId: this.authId ?? this.authData?.authorizationId,
      providerId: this.addAuthAllocationForm.value.pa,
      startDate: authStartDate ? this.dateTimeFormatter.formatToString(authStartDate) : '',
      endDate: authEndDate ? this.dateTimeFormatter.formatToString(authEndDate) : '',
      amount: this.addAuthAllocationForm.value.units,
      otherRate: this.addAuthAllocationForm.value.otherRate || false
    };

    this.authorizationService
      .addUpdateAllocation(this.programName, payload, action, this.currentAuthorizationProviderId, true)
      .subscribe((data: any) => {
        this.addAuthAllocationForm.get('pa')?.reset();
        this.fetchAuthAllocation();
        this.closeAddAuthAllocatiionDialog();
      });
  }

  /** Deactivate lives inside the allocation edit dialog to keep the row actions short. */
  confirmAllocationDeactivation() {
    const authorizationProviderId = this.currentAuthorizationProviderId;
    if (!authorizationProviderId) {
      return;
    }

    this.closeAddAuthAllocatiionDialog();
    this.onGridAction({ action: 'deactivate', data: { authorizationProviderId } });
  }

  handleCancel() {
    this.closeAddAuthAllocatiionDialog();
  }

  closeAddAuthAllocatiionDialog() {
    this.currentAuthorizationProviderId = null;
    if (this.addAuthAllocationDialog) {
      this.dialogSvc.closeDialog(this.addAuthAllocationDialog);
      this.addAuthAllocationDialog = null;
    }
  }

  onGridAction(event: any) {
    // Grid-level Add button on an allocation row — capture a new service detail for it.
    if (event?.action === 'add') {
      this.openServiceDetailDialog(event?.data, null);
      return;
    }

    // The expanded service detail rows expose the list-table's line item action,
    // which emits `view` with the line item as data and its parent row as param.
    if (event?.action === 'view') {
      this.openServiceDetailDialog(event?.param, event?.data);
      return;
    }

    if (event?.action === 'deactivate') {
      const dialogConfig: DialogConfig = {
        title: `Confirm Deactivation`,
        size: 'medium',
        position: 'top',
        okText: `Confirm`,
        showClose: true,
        showButtons: true,
      };

      this.confirmDialogId = this.dialogSvc.openDialog(
        this.confirmationDialog,
        dialogConfig,
        () => this.deactivateAllocation(event),
        () => this.dialogSvc.closeDialog(this.confirmDialogId)
      );

      return;
    }

    this.addAuthAllocationForm.patchValue({
      pa: parseInt(event?.data?.providerId),
      authStartDate: this.toDateOnly(event?.data?.startDate),
      authEndDate: this.toDateOnly(event?.data?.endDate),
      units: event?.data?.amount,
      otherRate: event?.data?.otherRate
    });

    this.currentAuthorizationProviderId = event?.data?.authorizationProviderId;

    this.addAllocation('Update');
  }

  getErrorMessage(controlName: string) {
    const control = this.addAuthAllocationForm.get(controlName);
    if (control?.errors) {
      return control.errors['message'] || '';
    }
    return '';
  }

  /**
   * A date with no time part, comparable against any other date from this form.
   *
   * The pickers hand back a Date at local midnight, while the authorization's own dates arrive
   * as `yyyy-MM-dd` strings — and `new Date('2026-09-01')` parses a date-only string as *UTC*
   * midnight, which is the previous day everywhere west of Greenwich. Comparing the two forms
   * directly was therefore off by the timezone offset, and formatting a UTC-parsed date with
   * the local-component formatters shifted it a day back on the way out.
   *
   * formatStringToDate builds the date from its parts in local time, so both forms land on the
   * same local midnight and only the calendar date is ever compared.
   */
  private toDateOnly(value: any): Date | null {
    if (!value) {
      return null;
    }

    if (value instanceof Date) {
      return new Date(value.getFullYear(), value.getMonth(), value.getDate());
    }

    const datePart = this.dateTimeFormatter.formatToDatePartString(String(value));
    const parsed = this.dateTimeFormatter.formatStringToDate(datePart);
    return isNaN(parsed.getTime()) ? null : parsed;
  }

  validateAllocationForm() {
    const form = this.addAuthAllocationForm.controls;
    const providerAuthStartDate = this.toDateOnly(form['authStartDate'].value);
    const providerAuthEndDate = this.toDateOnly(form['authEndDate'].value);
    const units = Number(form['units'].value) || 0;

    const authorizedStart = this.toDateOnly(this.authData.startDate);
    const authorizedEnd = this.toDateOnly(this.authData.endDate);
    const authorizedDollars = Number(this.authData.units) || 0;

    // 1. Validate start date
    if (providerAuthStartDate && authorizedStart && providerAuthStartDate < authorizedStart) {
      return {
        error: true,
        control: 'authStartDate',
        message: 'Allocation start date should be within the authorization start date'
      };
    }

    // 2. Validate end date
    if (providerAuthEndDate && authorizedEnd && providerAuthEndDate > authorizedEnd) {
      return {
        error: true,
        control: 'authEndDate',
        message: 'Allocation end date should be within the authorization end date'
      };
    }

    // 2.1. Validate start date
    if (providerAuthStartDate && providerAuthEndDate && providerAuthStartDate > providerAuthEndDate) {
      return {
        error: true,
        control: 'authStartDate',
        message: 'Allocation start date cannot be after authorization end date'
      };
    }

    // 1.1. Validate start date
    if (providerAuthEndDate && providerAuthStartDate && providerAuthEndDate < providerAuthStartDate) {
      return {
        error: true,
        control: 'authEndDate',
        message: 'Authorization end date cannot be before start date'
      };
    }

    // 3. Validate allocated dollars against authorized amount
    const paValue = this.addAuthAllocationForm.get('pa')?.value;
    if (units < 0) {
      return {
        error: true,
        control: 'units',
        message: 'Allocated dollars must be greater than or equal to zero'
      };
    }

    // 4. Validate allocated dollars against authorized amount
    if (units > authorizedDollars) {
      return {
        error: true,
        control: 'units',
        message: 'Allocated dollars exceed authorized allocation'
      };
    }

    // 5. Validate against existing allocations
    if (this.authAllocationData) {
      const isEditing = !!this.currentAuthorizationProviderId;

      // Total currently allocated (excluding current PA if editing)
      const filteredAllocations = isEditing
        ? this.authAllocationData.filter(
          (alloc: any) => alloc.authorizationProviderId !== this.currentAuthorizationProviderId
        )
        : this.authAllocationData;

      const existingTotalAllocated = filteredAllocations.filter((e: any) => e.active).reduce(
        (sum: number, alloc: any) => sum + (alloc.amount || 0),
        0
      );

      const totalWithNew = existingTotalAllocated + units;

      if (totalWithNew > authorizedDollars) {
        return {
          error: true,
          control: 'units',
          message: 'Reached maximum allocated dollars limit'
        };
      }
    }

    // ✅ Passed all validations
    return {
      error: false,
      control: '',
      message: 'Successfully validated'
    };
  }

  deactivateAllocation(event: any) {
    this.authorizationService
      .deactivateAllocation(this.programName, event?.data?.authorizationProviderId)
      .subscribe(() => {
        this.fetchAuthAllocation();
      });
  }

  validateProvider(event: any) {
    if (event !== null) {
      if (event !== 0) {
        this.addAuthAllocationForm.get('units')?.setValidators([Validators.required]);
      } else {
        this.addAuthAllocationForm.get('units')?.removeValidators([Validators.required]);
      }
    }
  }

  /** Expand icon on an allocation row — loads its service details the first time. */
  onRowExpand(row: any) {
    if (!row?.authorizationProviderId) {
      return;
    }

    if (row.loaded) {
      row.showLineItem = !row.showLineItem;
      return;
    }

    this.fetchServiceDetails(row);
  }

  fetchServiceDetails(row: any, expand = true) {
    const processedPayload = this.buildRequestPayload(
      'consumer-auth-prvdr-service-dtl',
      'modifiedDate',
      [{ jsonPath: "authorizationProviderId", value: row.authorizationProviderId, operator: "Equal" }]
    );

    this.listService
      .getListData(processedPayload, '')
      .subscribe((data: any) => {
        row.lineItems = (data?.responsedata ?? [])
          .filter((service: any) => service.active !== false)
          .map((service: any) => ({
            ...toServiceDetailRow(service),
            editAction: 'Edit',
          }));
        row.loaded = true;
        row.showLineItem = expand;
        // The table is OnPush and only notices showLineItem flipping, so a reload that
        // leaves the row expanded (after an add/update/deactivate) needs a nudge.
        this.allocationTable?.refresh();
      });
  }

  /** Edit/Deactivate in the expanded service detail rows are rendered as clickable cells. */
  onCellClicked(event: any) {
    const serviceDetail = event?.rowData;
    if (!serviceDetail?.authProviderServiceId) {
      return;
    }

    const allocation = this.authAllocationData?.find(
      (row: any) => row.authorizationProviderId === serviceDetail.authorizationProviderId
    );

    if (event?.col?.field === 'editAction') {
      // Load the record fresh so the form edits current server state.
      this.authorizationService
        .getServiceDetail(this.programName, serviceDetail.authProviderServiceId)
        .subscribe((data: any) => {
          this.openServiceDetailDialog(allocation, data?.responsedata ?? serviceDetail);
        });
    }
  }

  openServiceDetailDialog(allocation: any, serviceDetail: any) {
    if (!allocation?.authorizationProviderId) {
      return;
    }

    this.currentServiceAllocation = allocation;
    this.currentServiceDetail = serviceDetail ?? null;

    const action = this.currentServiceDetail === null ? 'Add' : 'Edit';
    const dialogConfig: DialogConfig = {
      title: `${action} Service Detail`,
      size: 'medium',
      position: 'top',
      okText: `${action}`,
      showClose: true,
      showButtons: false,
    };

    this.addServiceDetailDialog = this.dialogSvc.openDialog(
      this.serviceDetailDialog,
      dialogConfig,
      () => this.closeServiceDetailDialog(),
      () => this.closeServiceDetailDialog()
    );
  }

  onServiceDetailSaved(payload: AuthServiceDetailPayload) {
    const allocation = this.currentServiceAllocation;
    const action = this.currentServiceDetail === null ? 'Add' : 'Update';

    this.authorizationService
      .addUpdateServiceDetail(
        this.programName,
        payload,
        action,
        this.currentServiceDetail?.authProviderServiceId
      )
      .subscribe(() => {
        this.closeServiceDetailDialog();
        this.fetchServiceDetails(allocation);
      });
  }

  /** Called from the subrow's Deactivate cell, or from the edit dialog with no arguments. */
  confirmServiceDetailDeactivation(allocation = this.currentServiceAllocation, serviceDetail = this.currentServiceDetail) {
    const authProviderServiceId = serviceDetail?.authProviderServiceId;
    if (!authProviderServiceId) {
      return;
    }

    this.closeServiceDetailDialog();

    const dialogConfig: DialogConfig = {
      title: `Confirm Deactivation`,
      size: 'medium',
      position: 'top',
      okText: `Confirm`,
      showClose: true,
      showButtons: true,
    };

    this.confirmServiceDetailDialogId = this.dialogSvc.openDialog(
      this.serviceDetailConfirmationDialog,
      dialogConfig,
      () => this.deactivateServiceDetail(allocation, authProviderServiceId),
      () => this.dialogSvc.closeDialog(this.confirmServiceDetailDialogId)
    );
  }

  deactivateServiceDetail(allocation: any, authProviderServiceId: any) {
    this.authorizationService
      .deactivateServiceDetail(this.programName, authProviderServiceId)
      .subscribe(() => {
        this.fetchServiceDetails(allocation);
      });
  }

  closeServiceDetailDialog() {
    this.currentServiceAllocation = null;
    this.currentServiceDetail = null;
    if (this.addServiceDetailDialog) {
      this.dialogSvc.closeDialog(this.addServiceDetailDialog);
      this.addServiceDetailDialog = null;
    }
  }
}
